import { Injectable } from '@angular/core';

import { DiffChange } from './diff-change.model';
import { DiffChangeType } from './diff-change-type.enum';
import { DiffOptions } from './diff-options.model';

@Injectable()
export class ObjectDiffService {

  detectChanges<T extends Record<string, any>>(
    previous: T | null,
    current: T,
    options: DiffOptions = {},
  ): DiffChange[] {
    if (!previous) {
      return [];
    }

    const changes: DiffChange[] = [];
    const { ignoreFields = [], deepCompare = true, trackArraysByIndex = true } = options;

    this.compareObjects(previous, current, '', changes, ignoreFields, deepCompare, trackArraysByIndex, options);

    return changes;
  }

  private compareObjects(
    previous: any,
    current: any,
    path: string,
    changes: DiffChange[],
    ignoreFields: string[],
    deepCompare: boolean,
    trackArraysByIndex: boolean,
    options: DiffOptions,
  ): void {
    if (previous === current) {
      return;
    }

    if (this.isPrimitive(previous) || this.isPrimitive(current)) {
      // The identical-values case already returned above, so the operands here
      // always differ - record the field change unconditionally.
      changes.push({
        type: DiffChangeType.Field,
        path: path || 'root',
        oldValue: previous,
        newValue: current,
      });
      return;
    }

    if (Array.isArray(previous) && Array.isArray(current)) {
      this.compareArrays(previous, current, path, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
      return;
    }

    // isPrimitive() above already filtered out everything whose typeof is not
    // 'object' (including null/undefined/functions) and the array case returned,
    // so both operands are objects here - compare their properties directly.
    this.compareObjectProperties(previous, current, path, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
  }

  private compareArrays(
    previous: any[],
    current: any[],
    path: string,
    changes: DiffChange[],
    ignoreFields: string[],
    deepCompare: boolean,
    trackArraysByIndex: boolean,
    options: DiffOptions,
  ): void {
    if (trackArraysByIndex) {
      this.compareArraysByIndex(previous, current, path, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
    } else {
      this.compareArraysByLength(previous, current, path, changes);
    }
  }

  private compareArraysByIndex(
    previous: any[],
    current: any[],
    path: string,
    changes: DiffChange[],
    ignoreFields: string[],
    deepCompare: boolean,
    trackArraysByIndex: boolean,
    options: DiffOptions,
  ): void {
    // Build item matching map
    const currToPrevMap = this.buildItemMatchingMaps(previous, current, deepCompare, options.idField);
    
    // Generate changes based on matches
    this.generateArrayChanges(previous, current, path, changes, ignoreFields, deepCompare, trackArraysByIndex, currToPrevMap, options);
  }

  private buildItemMatchingMaps(previous: any[], current: any[], deepCompare: boolean, idField?: string) {
    const matchedPrevIndices = new Set<number>();
    const currToPrevMap = new Map<number, number>();
    
    if (idField) {
      // Use ID-based matching for better move+modify support
      this.matchItemsByIds(previous, current, idField, matchedPrevIndices, currToPrevMap);
    } else {
      // Use exact equality matching (original behavior)
      // First: match items at same positions
      this.matchItemsAtSamePosition(previous, current, deepCompare, matchedPrevIndices, currToPrevMap);
      
      // Second: find items that moved positions  
      this.matchMovedItems(previous, current, deepCompare, matchedPrevIndices, currToPrevMap);
    }
    
    return currToPrevMap;
  }

  private matchItemsAtSamePosition(
    previous: any[], 
    current: any[], 
    deepCompare: boolean, 
    matchedPrevIndices: Set<number>, 
    currToPrevMap: Map<number, number>
  ): void {
    for (let i = 0; i < Math.min(previous.length, current.length); i++) {
      if (this.itemsAreEqual(previous[i], current[i], deepCompare)) {
        matchedPrevIndices.add(i);
        currToPrevMap.set(i, i);
      }
    }
  }

  private matchMovedItems(
    previous: any[], 
    current: any[], 
    deepCompare: boolean, 
    matchedPrevIndices: Set<number>, 
    currToPrevMap: Map<number, number>
  ): void {
    current.forEach((currItem, currIndex) => {
      if (currToPrevMap.has(currIndex)) return; // Already matched
      
      // Find matching item in previous array
      for (let prevIndex = 0; prevIndex < previous.length; prevIndex++) {
        if (!matchedPrevIndices.has(prevIndex) && this.itemsAreEqual(previous[prevIndex], currItem, deepCompare)) {
          matchedPrevIndices.add(prevIndex);
          currToPrevMap.set(currIndex, prevIndex);
          break;
        }
      }
    });
  }

  private matchItemsByIds(
    previous: any[], 
    current: any[], 
    idField: string, 
    matchedPrevIndices: Set<number>, 
    currToPrevMap: Map<number, number>
  ): void {
    // Build maps of ID to indices for fast lookup
    const prevIdToIndex = new Map<any, number>();
    const currIdToIndex = new Map<any, number>();
    
    // Build previous ID map
    previous.forEach((item, index) => {
      if (typeof item === 'object' && item != null && item[idField] != null) {
        prevIdToIndex.set(item[idField], index);
      }
    });
    
    // Match current items to previous by ID  
    current.forEach((item, currIndex) => {
      if (typeof item === 'object' && item != null && item[idField] != null) {
        const id = item[idField];
        const prevIndex = prevIdToIndex.get(id);
        
        if (prevIndex !== undefined && !matchedPrevIndices.has(prevIndex)) {
          // Found matching ID - match them regardless of position
          matchedPrevIndices.add(prevIndex);
          currToPrevMap.set(currIndex, prevIndex);
        }
      }
    });
  }

  private itemsAreEqual(item1: any, item2: any, deepCompare: boolean): boolean {
    if (deepCompare && typeof item1 === 'object' && typeof item2 === 'object' && item1 != null && item2 != null) {
      return JSON.stringify(item1) === JSON.stringify(item2);
    }
    return item1 === item2;
  }

  private generateArrayChanges(
    previous: any[], 
    current: any[], 
    path: string, 
    changes: DiffChange[], 
    ignoreFields: string[], 
    deepCompare: boolean, 
    trackArraysByIndex: boolean, 
    currToPrevMap: Map<number, number>,
    options: DiffOptions
  ): void {
    if (options.idField) {
      this.generateIdBasedArrayChanges(previous, current, path, changes, ignoreFields, deepCompare, trackArraysByIndex, currToPrevMap, options);
    } else {
      this.generatePositionBasedArrayChanges(previous, current, path, changes, ignoreFields, deepCompare, trackArraysByIndex, currToPrevMap, options);
    }
  }

  private generatePositionBasedArrayChanges(
    previous: any[], 
    current: any[], 
    path: string, 
    changes: DiffChange[], 
    ignoreFields: string[], 
    deepCompare: boolean, 
    trackArraysByIndex: boolean, 
    currToPrevMap: Map<number, number>,
    options: DiffOptions
  ): void {
    const maxLength = Math.max(previous.length, current.length);
    
    for (let i = 0; i < maxLength; i++) {
      const prevItem = i < previous.length ? previous[i] : undefined;
      const currItem = i < current.length ? current[i] : undefined;
      
      if (prevItem === undefined && currItem !== undefined) {
        this.addArrayItemChange(changes, DiffChangeType.ArrayItemAdded, path, i, undefined, currItem);
      } else if (prevItem !== undefined && currItem === undefined) {
        this.addArrayItemChange(changes, DiffChangeType.ArrayItemRemoved, path, i, prevItem, undefined);
      } else if (prevItem != null && currItem != null) {
        this.handleItemComparison(prevItem, currItem, i, path, changes, ignoreFields, deepCompare, trackArraysByIndex, currToPrevMap, options);
      }
    }
  }

  private generateIdBasedArrayChanges(
    previous: any[], 
    current: any[], 
    path: string, 
    changes: DiffChange[], 
    ignoreFields: string[], 
    deepCompare: boolean, 
    trackArraysByIndex: boolean, 
    currToPrevMap: Map<number, number>,
    options: DiffOptions
  ): void {
    const idField = options.idField!;
    const matchedPrevIndices = new Set<number>();
    
    // First pass: identify matched items and check for field changes
    current.forEach((currItem, currIndex) => {
      const prevIndex = currToPrevMap.get(currIndex);
      
      if (prevIndex !== undefined) {
        // This current item has a match in previous array
        matchedPrevIndices.add(prevIndex);
        const prevItem = previous[prevIndex];
        
        // Compare the matched items for field changes
        this.compareItemFields(prevItem, currItem, currIndex, path, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
      } else {
        // This current item has no match - it's a new item
        this.addArrayItemChange(changes, DiffChangeType.ArrayItemAdded, path, currIndex, undefined, currItem);
      }
    });
    
    // Second pass: find removed items (items in previous that were not matched)
    previous.forEach((prevItem, prevIndex) => {
      if (!matchedPrevIndices.has(prevIndex)) {
        // This previous item has no match in current array - it was removed
        this.addArrayItemChange(changes, DiffChangeType.ArrayItemRemoved, path, prevIndex, prevItem, undefined);
      }
    });
  }

  private addArrayItemChange(
    changes: DiffChange[], 
    type: DiffChangeType, 
    path: string, 
    index: number, 
    oldValue: any, 
    newValue: any
  ): void {
    const change: DiffChange = { type, path, arrayIndex: index };
    if (oldValue !== undefined) change.oldValue = oldValue;
    if (newValue !== undefined) change.newValue = newValue;
    changes.push(change);
  }

  private handleItemComparison(
    prevItem: any, 
    currItem: any, 
    index: number, 
    path: string, 
    changes: DiffChange[], 
    ignoreFields: string[], 
    deepCompare: boolean, 
    trackArraysByIndex: boolean, 
    currToPrevMap: Map<number, number>,
    options: DiffOptions
  ): void {
    const currMatchedToPrev = currToPrevMap.get(index);
    
    if (currMatchedToPrev === index) {
      // Same item at same position - check for field changes
      this.compareItemFields(prevItem, currItem, index, path, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
    } else if (currToPrevMap.has(index)) {
      // Item moved here from different position - no field changes to report
      return;
    } else {
      // Unmatched items - truly different
      this.compareItemFields(prevItem, currItem, index, path, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
    }
  }

  private compareItemFields(
    prevItem: any, 
    currItem: any, 
    index: number, 
    path: string, 
    changes: DiffChange[], 
    ignoreFields: string[], 
    deepCompare: boolean, 
    trackArraysByIndex: boolean,
    options: DiffOptions
  ): void {
    if (deepCompare && typeof prevItem === 'object' && typeof currItem === 'object') {
      const allKeys = new Set([...Object.keys(prevItem), ...Object.keys(currItem)]);
      
      allKeys.forEach(key => {
        if (ignoreFields.includes(key)) return;
        
        const prevValue = prevItem[key];
        const currValue = currItem[key];
        
        if (Array.isArray(prevValue) && Array.isArray(currValue)) {
          const nestedPath = `${path}[${index}].${key}`;
          this.compareArrays(prevValue, currValue, nestedPath, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
        } else if (prevValue !== currValue) {
          changes.push({
            type: DiffChangeType.Field,
            path,
            arrayIndex: index,
            fieldName: key,
            oldValue: prevValue,
            newValue: currValue,
          });
        }
      });
    } else if (prevItem !== currItem) {
      changes.push({
        type: DiffChangeType.ArrayItemModified,
        path,
        arrayIndex: index,
        oldValue: prevItem,
        newValue: currItem,
      });
    }
  }

  private compareArraysByLength(
    previous: any[],
    current: any[],
    path: string,
    changes: DiffChange[],
  ): void {
    if (previous.length !== current.length) {
      if (previous.length < current.length) {
        for (let i = previous.length; i < current.length; i++) {
          changes.push({
            type: DiffChangeType.ArrayItemAdded,
            path,
            arrayIndex: i,
            newValue: current[i],
          });
        }
      } else {
        for (let i = current.length; i < previous.length; i++) {
          changes.push({
            type: DiffChangeType.ArrayItemRemoved,
            path,
            arrayIndex: i,
            oldValue: previous[i],
          });
        }
      }
    }
  }

  private compareObjectProperties(
    previous: Record<string, any>,
    current: Record<string, any>,
    path: string,
    changes: DiffChange[],
    ignoreFields: string[],
    deepCompare: boolean,
    trackArraysByIndex: boolean,
    options: DiffOptions,
  ): void {
    const allKeys = new Set([ ...Object.keys(previous), ...Object.keys(current) ]);

    allKeys.forEach(key => {
      if (ignoreFields.includes(key)) {
        return;
      }

      const fieldPath = path ? `${ path }.${ key }` : key;
      const prevValue = previous[key];
      const currValue = current[key];

      if (deepCompare &&
        typeof prevValue ===
        'object' &&
        typeof currValue ===
        'object' &&
        prevValue !==
        null &&
        currValue !==
        null) {
        this.compareObjects(prevValue, currValue, fieldPath, changes, ignoreFields, deepCompare, trackArraysByIndex, options);
      } else if (prevValue !== currValue) {
        changes.push({
          type: DiffChangeType.Field,
          path: fieldPath,
          fieldName: key,
          oldValue: prevValue,
          newValue: currValue,
        });
      }
    });
  }

  private isPrimitive(value: any): boolean {
    return value === null || value === undefined || typeof value !== 'object';
  }

  deepClone<T>(obj: T): T {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      return obj;
    }
    return JSON.parse(JSON.stringify(obj));
  }
}
